import React from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip, ReferenceArea, YAxis, XAxis, CartesianGrid } from 'recharts';
import { formatDate } from '../../util/GenericUtil';
import { convertDateToTimestamp, applyMinimumGapConstraint } from '../../util/TimeBasedScaling';
import { parseRangeValue, parseResultValue, computeRangeZones } from '../../util/RangeZones';
import './DetailedTestChart.css';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="detailedChartTooltip">
                <p className="tooltipDate">
                    {data.testdate ? formatDate(String(data.testdate)) : 'No Date'}
                </p>
                <p className="tooltipValue">
                    {data.result}
                </p>
            </div>
        );
    }
    return null;
};

function DetailedTestChart({ test, fxRange, labLow, labHigh }) {
    const resultValues = (test.results || []).map(r => {
        const raw = typeof r === 'object' ? r.result : r;
        return parseResultValue(raw) ?? 0;
    });

    // Convert testdates to timestamps and apply shared time-based scaling
    const dataWithTimestamps = (test.results || []).map(r => ({
        ...r,
        timestamp: convertDateToTimestamp(String(r.testdate || r.date || '')),
        result: parseResultValue(typeof r === 'object' ? r.result : r) ?? 0
    })).sort((a, b) => a.timestamp - b.timestamp);

    const adjustedData = applyMinimumGapConstraint(dataWithTimestamps);

    // Calculate domain from result values only — ranges never affect the y-axis
    const minResult = resultValues.length > 0 ? Math.min(...resultValues) : 0;
    const maxResult = resultValues.length > 0 ? Math.max(...resultValues) : 100;
    const range = maxResult - minResult;
    const padding = range > 0 ? range * 0.15 : (minResult === 0 ? 0.5 : Math.abs(minResult) * 0.15);
    const domainMin = minResult - padding;
    const domainMax = maxResult + padding;

    // Generate y-axis ticks at the result's decimal precision (capped at 4 to avoid
    // floating-point artifacts from unit conversion producing near-infinite loops)
    const maxDecimals = Math.min(4, resultValues.reduce((mx, v) => {
        const str = String(v);
        const idx = str.indexOf('.');
        return Math.max(mx, idx >= 0 ? str.length - idx - 1 : 0);
    }, 0));
    const yRange = domainMax - domainMin;
    const unit = Math.pow(10, -maxDecimals);
    const candidates = [1 * unit, 2 * unit, 5 * unit, 10 * unit, 20 * unit, 50 * unit];
    let tickStep = candidates[candidates.length - 1];
    for (const c of candidates) {
        if (yRange / c <= 8) { tickStep = c; break; }
    }
    const firstTick = Math.ceil(domainMin / tickStep) * tickStep;
    const yTicks = [];
    for (let v = firstTick; v <= domainMax + tickStep * 0.001 && yTicks.length < 20; v += tickStep) {
        yTicks.push(Number(v.toFixed(maxDecimals)));
    }

    // Compute range zones using shared helper (mirrors backend get_marker_direction)
    const zones = computeRangeZones({
        fxLow: parseRangeValue(fxRange?.fx_low),
        fxHigh: parseRangeValue(fxRange?.fx_high),
        labLow: parseRangeValue(labLow),
        labHigh: parseRangeValue(labHigh),
        domainMin,
        domainMax,
    });

    return (
        <div className="detailedTestChart">
            <div className="chartContainer">
                {adjustedData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={adjustedData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis
                                dataKey="adjustedTimestamp"
                                type="number"
                                domain={['dataMin', 'dataMax']}
                                tickFormatter={(timestamp) => {
                                    const date = new Date(timestamp);
                                    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                                }}
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                axisLine={{ stroke: '#d1d5db' }}
                            />
                            <YAxis
                                domain={[domainMin, domainMax]}
                                ticks={yTicks}
                                tickFormatter={(v) => v.toFixed(maxDecimals)}
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                axisLine={{ stroke: '#d1d5db' }}
                                width={40}
                            />
                            {/* Red: abnormal zones outside lab range */}
                            {zones.abnormalLow && <ReferenceArea y1={zones.abnormalLow.y1} y2={zones.abnormalLow.y2} fill="#ef4444" fillOpacity={0.12} />}
                            {zones.abnormalHigh && <ReferenceArea y1={zones.abnormalHigh.y1} y2={zones.abnormalHigh.y2} fill="#ef4444" fillOpacity={0.12} />}
                            {/* Yellow: dysfunctional zones between lab and FX */}
                            {zones.dysfunctionalLow && <ReferenceArea y1={zones.dysfunctionalLow.y1} y2={zones.dysfunctionalLow.y2} fill="#eab308" fillOpacity={0.15} />}
                            {zones.dysfunctionalHigh && <ReferenceArea y1={zones.dysfunctionalHigh.y1} y2={zones.dysfunctionalHigh.y2} fill="#eab308" fillOpacity={0.15} />}
                            {/* Green: optimal FX range */}
                            {zones.optimal && <ReferenceArea y1={zones.optimal.y1} y2={zones.optimal.y2} fill="#22c55e" fillOpacity={0.15} />}
                            <Line
                                type="monotone"
                                dataKey="result"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                dot={{ fill: '#3b82f6', r: 4 }}
                                activeDot={{ r: 6, fill: '#2563eb' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="noData">No historical data available</p>
                )}
            </div>
        </div>
    );
}

export default DetailedTestChart;
